import { ProviderInteractionMode, RuntimeMode } from "@cmmd-forge/contracts";
import { memo, type ReactNode } from "react";
import { EllipsisIcon, ListTodoIcon } from "lucide-react";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator as MenuDivider,
  MenuTrigger,
} from "../ui/menu";
import { useSettings } from "~/hooks/useSettings";
import { composerFloatingLayerProps } from "./composerEventScope";
import {
  COMPOSER_STASH_MENU_POPUP_CLASS,
  ComposerStashMenuItems,
  type ComposerStashControlProps,
} from "./ComposerStashMenu";

export const CompactComposerControlsMenu = memo(function CompactComposerControlsMenu(props: {
  activePlan: boolean;
  interactionMode: ProviderInteractionMode;
  planSidebarLabel: string;
  planSidebarOpen: boolean;
  runtimeMode: RuntimeMode;
  traitsMenuContent?: ReactNode;
  stash?: ComposerStashControlProps | null;
  onToggleInteractionMode: () => void;
  onTogglePlanSidebar: () => void;
  onRuntimeModeChange: (mode: RuntimeMode) => void;
}) {
  const showInteractionModeToggle = useSettings((s) => s.showInteractionModeToggle);

  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            // `icon-sm`, not `sm`. A `sm` Button pins `h-8`, which survives the
            // shared icon class and left this trigger 48px wide and 32px tall
            // on a phone. Measured, not inferred. The icon sizes carry no
            // height of their own to fight the touch square with.
            size="icon-sm"
            variant="ghost"
            className={cn(
              RESPONSIVE_ICON_BUTTON_CLASS,
              "text-muted-foreground/70 hover:text-foreground/80",
            )}
            aria-label="More composer controls"
          />
        }
      >
        <EllipsisIcon aria-hidden="true" className="size-4" />
      </MenuTrigger>
      <MenuPopup
        align="start"
        className={COMPOSER_STASH_MENU_POPUP_CLASS}
        {...composerFloatingLayerProps}
      >
        {props.traitsMenuContent ? (
          <>
            {props.traitsMenuContent}
            <MenuDivider />
          </>
        ) : null}
        {props.stash ? (
          <>
            <ComposerStashMenuItems {...props.stash} />
            <MenuDivider />
          </>
        ) : null}
        {showInteractionModeToggle ? (
          <>
            <div className="px-2 py-1.5 font-medium text-muted-foreground text-xs">Mode</div>
            <MenuRadioGroup
              value={props.interactionMode}
              onValueChange={(value) => {
                if (!value || value === props.interactionMode) return;
                props.onToggleInteractionMode();
              }}
            >
              <MenuRadioItem value="default">Chat</MenuRadioItem>
              <MenuRadioItem value="plan">Plan</MenuRadioItem>
            </MenuRadioGroup>
            <MenuDivider />
          </>
        ) : null}
        <div className="px-2 py-1.5 font-medium text-muted-foreground text-xs">Access</div>
        <MenuRadioGroup
          value={props.runtimeMode}
          onValueChange={(value) => {
            if (!value || value === props.runtimeMode) return;
            props.onRuntimeModeChange(value as RuntimeMode);
          }}
        >
          <MenuRadioItem value="approval-required">Supervised</MenuRadioItem>
          <MenuRadioItem value="auto-accept-edits">Auto-accept edits</MenuRadioItem>
          <MenuRadioItem value="full-access">Full access</MenuRadioItem>
        </MenuRadioGroup>
        {props.activePlan ? (
          <>
            <MenuDivider />
            <MenuItem onClick={props.onTogglePlanSidebar}>
              <ListTodoIcon className="size-4 shrink-0" />
              {props.planSidebarOpen
                ? `Hide ${props.planSidebarLabel.toLowerCase()} sidebar`
                : `Show ${props.planSidebarLabel.toLowerCase()} sidebar`}
            </MenuItem>
          </>
        ) : null}
      </MenuPopup>
    </Menu>
  );
});
